/**
 * Katalog menu ACL + helper path.
 * Sumber: utils/sidebarMenu (satu sumber kebenaran, aman di Nitro).
 */
import sidebarItem, { type menu } from './sidebarMenu';

export type MenuAclNode = {
  key: string;
  path: string;
  titleKey?: string;
  title?: string;
  icon?: string;
  children?: MenuAclNode[];
};

function walk(items: menu[], out: MenuAclNode[] = []): MenuAclNode[] {
  for (const item of items) {
    if (item.header || item.headerKey) continue;
    if (item.children?.length) {
      const kids = walk(item.children, []);
      if (item.to) {
        out.push({
          key: item.to,
          path: item.to,
          titleKey: item.titleKey,
          title: item.title,
          icon: typeof item.icon === 'string' ? item.icon : undefined,
          children: kids.length ? kids : undefined,
        });
      } else if (kids.length) {
        out.push({
          key: `group:${item.titleKey || item.title || out.length}`,
          path: '',
          titleKey: item.titleKey,
          title: item.title,
          icon: typeof item.icon === 'string' ? item.icon : undefined,
          children: kids,
        });
      }
    } else if (item.to) {
      out.push({
        key: item.to,
        path: item.to,
        titleKey: item.titleKey,
        title: item.title,
        icon: typeof item.icon === 'string' ? item.icon : undefined,
      });
    }
  }
  return out;
}

export function getMenuAclTree(): MenuAclNode[] {
  return walk(sidebarItem);
}

export function flattenMenuPaths(tree: MenuAclNode[] = getMenuAclTree()): string[] {
  const out: string[] = [];
  const visit = (nodes: MenuAclNode[]) => {
    for (const n of nodes) {
      if (n.path) out.push(n.path);
      if (n.children?.length) visit(n.children);
    }
  };
  visit(tree);
  return [...new Set(out)];
}

export function parseMenusJson(raw: string | null | undefined): string[] | '*' {
  try {
    const arr = JSON.parse(String(raw || '[]'));
    if (!Array.isArray(arr)) return [];
    const list = arr.map((x) => String(x || '').trim()).filter(Boolean);
    if (list.includes('*')) return '*';
    return [...new Set(list)];
  } catch {
    return [];
  }
}

export function menusToJson(menus: string[] | '*'): string {
  if (menus === '*' || (Array.isArray(menus) && menus.includes('*'))) {
    return JSON.stringify(['*']);
  }
  return JSON.stringify([...(menus || [])].filter(Boolean));
}

/** Normalisasi path aplikasi */
export function normalizeAppPath(path: string): string {
  let p = String(path || '').split('?')[0].split('#')[0].trim();
  if (!p) return '/';
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

export function pathAllowed(path: string, menus: string[] | '*'): boolean {
  if (menus === '*' || (Array.isArray(menus) && menus.includes('*'))) return true;
  const p = normalizeAppPath(path);
  // Halaman akun pribadi selalu diizinkan untuk user terautentikasi
  if (p === '/account' || p.startsWith('/account/')) return true;
  const list = (menus || []).map(normalizeAppPath);
  if (list.includes(p)) return true;
  // child of allowed parent hub
  return list.some((m) => m !== '/' && (p === m || p.startsWith(`${m}/`)));
}

/** Default menu set per level code */
export function defaultMenusForLevel(code: string): string[] | '*' {
  const all = flattenMenuPaths();
  switch (code) {
    case 'system':
    case 'admin':
      return '*';
    case 'accountant':
      return all.filter(
        (p) =>
          !p.startsWith('/users') &&
          !p.startsWith('/backup') &&
          !p.startsWith('/tasya') &&
          p !== '/audit'
      );
    case 'operator':
      return all.filter((p) =>
        [
          '/',
          '/calendar',
          '/quick-journal',
          '/journals',
          '/journals/entry',
          '/journals/lookup',
          '/statements',
          '/import',
          '/reconciliation',
          '/cash-bank',
          '/bank-accounts',
          '/reports',
          '/reports/downloads',
        ].includes(p) || p.startsWith('/reports/')
      );
    case 'viewer':
      return all.filter(
        (p) =>
          p === '/' ||
          p === '/calendar' ||
          p.startsWith('/reports') ||
          p === '/tax' ||
          p.startsWith('/tax/')
      );
    case 'staff':
      return all.filter(
        (p) =>
          p === '/' ||
          p === '/calendar' ||
          p === '/hr/me' ||
          p.startsWith('/hr/me') ||
          p === '/hr/attendance' ||
          p.startsWith('/hr/kasbon-qr') ||
          p === '/tax/requests' ||
          p.startsWith('/tax/requests/')
      );
    case 'customer':
      return all.filter(
        (p) =>
          p === '/' ||
          p === '/portal' ||
          p.startsWith('/portal/') ||
          p === '/tax/requests/me' ||
          p.startsWith('/tax/requests/me') ||
          p === '/account' ||
          p.startsWith('/account/')
      );
    default:
      return ['/'];
  }
}
